import { EmbedType } from '@/modules/elements/models/Embed'

const LOOM_BASE_REGEX_STR =
  '(?:https?://)?((?:stage.loom.com|loom.com|www.loom.com|loomlocal.com:4444)/(share|embed)/[a-f0-9]+)'

const YOUTUBE_BASE_REGEX_STR =
  '^((?:https?:)?\\/\\/)?((?:www|m)\\.)?((?:youtube(-nocookie)?\\.com|youtu.be))(\\/(?:[\\w\\-]+\\?v=|embed\\/|v\\/)?)([\\w\\-]+)(\\S+)?$'

const VIMEO_BASE_REGEX_STR =
  'https?://(?:www\\.|player\\.)?vimeo\\.com/(?:channels/\\w+/|groups/\\w+/videos/|video/)?(\\d+)(?:/(\\w+))?'

const GOOGLE_FORMS_BASE_REGEX_STR = '(?:http|https)?:?\\/?\\/?(?:www.)?(?:docs.google.com/forms/.*)'

const PITCH_BASE_REGEX_STR =
  '(?:https?://)?(app.)?(?:pitch.com)/(v|public|app/player|app/presentation|app/public/player)/(.*)'

const TYPEFORM_BASE_REGEX_STR = '(?:https?://)?([a-zA-Z0-9]+.typeform.com/to/[a-zA-Z0-9]+.*)'

const PRODUCT_BOARD_REGEX_STR = '(?:http|https)?:?\\/?\\/?(?:www.)?(?:portal.productboard.com/.*)'

const THREEDVISTA_REGEX_STR = 'https://storage.net-fs.com/hosting/\\d+/\\d+(?:/.*)?(?:#.+)?'

const RECLAIM_REGEX_STR = 'https://app\\.reclaim\\.ai/e/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}'

const UCARE_REGEX_STR = 'https://ucarecdn\\.com/[a-fA-F0-9\\-]+/[^/]+\\.(mp4|avi|mkv|webm)'

const CALENDLY_REGEX_STR = '(?:http|https)?:?\\/?\\/?(?:www.)?(?:calendly.com/.*)'

const CANVA_BASE_REGEX_STR = '(?:http|https)?:?\\/?\\/?(?:www.)?(?:canva.com\\/design\\/[^\\/]+\\/[^\\/]+\\/view)'

const URL_REGEX_STR =
  '(https?://(?:www.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^s]{2,}|www.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^s]{2,}|https?://(?:www.|(?!www))[a-zA-Z0-9]+.[^s]{2,}|www.[a-zA-Z0-9]+.[^s]{2,})'

const UUID_LENGTH = 36

export interface EmbedLinkMatch {
  original: string
  embed: string
  type: EmbedType
  genericUrl?: boolean
}

export function isGoogleFormsLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${GOOGLE_FORMS_BASE_REGEX_STR}`, 'g'))
}
export function isUrl(link: string): boolean {
  const matchesUrl = link.match(new RegExp(`${URL_REGEX_STR}`, 'g'))
  return matchesUrl?.length === 1
}

export function isLoomLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${LOOM_BASE_REGEX_STR}`, 'g'))
}
export function isYoutubeLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${YOUTUBE_BASE_REGEX_STR}`, 'g'))
}
export function isVimeoLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(VIMEO_BASE_REGEX_STR))
}
export function isPitchLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${PITCH_BASE_REGEX_STR}`, 'g'))
}
export function isTypeformLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${TYPEFORM_BASE_REGEX_STR}`, 'g'))
}
export function isProductBoardLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${PRODUCT_BOARD_REGEX_STR}`, 'g'))
}

export function isThreeDVistaLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${THREEDVISTA_REGEX_STR}`, 'g'))
}

export function isReclaimLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${RECLAIM_REGEX_STR}`, 'g'))
}

export function isUcareLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${UCARE_REGEX_STR}`, 'g'))
}

export function isCalendlyLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(CALENDLY_REGEX_STR, 'g'))
}

export function isCanvaLink(link: string): RegExpMatchArray | null {
  return link.match(new RegExp(`${CANVA_BASE_REGEX_STR}`, 'g'))
}

export function findPitchLink(link: string): EmbedLinkMatch | null {
  const matchesPitch = isPitchLink(link)
  if (!matchesPitch?.length) {
    return null
  }

  let embedUrl = matchesPitch[0]
  const publicPath = 'public'
  const appPitchUrl = 'app.pitch.com'
  const pitchUrl = 'pitch.com'

  if (embedUrl.includes('app/public/player')) {
    embedUrl = embedUrl.replace(appPitchUrl, pitchUrl).replace('app/public/player', publicPath)
  } else if (!embedUrl.includes('public') && !embedUrl.includes('/v/')) {
    const publicEmbedUrl = embedUrl
      .replace(appPitchUrl, pitchUrl)
      .replace('app/player', publicPath)
      .replace('app/presentation', publicPath)

    const teamIdIndex = publicEmbedUrl.indexOf(publicPath) + publicPath.length
    const teamIdPath = publicEmbedUrl.slice(teamIdIndex, teamIdIndex + UUID_LENGTH + 1)

    embedUrl = publicEmbedUrl.replace(teamIdPath, '')
  }

  return {
    original: matchesPitch[0],
    embed: embedUrl,
    type: EmbedType.PITCH
  }
}

export function findLoomLink(link: string): EmbedLinkMatch | null {
  const matchesLoom = isLoomLink(link)

  if (matchesLoom?.length) {
    return { original: matchesLoom[0], embed: matchesLoom[0].replace('share', 'embed'), type: EmbedType.LOOM }
  } else {
    return null
  }
}

export function findTypeformLink(link: string): EmbedLinkMatch | null {
  const matchesTypeform = isTypeformLink(link)

  if (matchesTypeform?.length) {
    return {
      original: matchesTypeform[0],
      embed: matchesTypeform[0],
      type: EmbedType.TYPEFORM
    }
  } else {
    return null
  }
}

function findProductboardLink(link: string): EmbedLinkMatch | null {
  const matchesProductboard = isProductBoardLink(link)

  if (matchesProductboard?.length) {
    return {
      original: matchesProductboard[0],
      embed: matchesProductboard[0],
      type: EmbedType.GENERIC
    }
  } else {
    return null
  }
}

function findThreeDVistaLink(link: string): EmbedLinkMatch | null {
  const matchesThreeDVista = isThreeDVistaLink(link)

  if (matchesThreeDVista?.length) {
    return {
      original: matchesThreeDVista[0],
      embed: matchesThreeDVista[0],
      type: EmbedType.GENERIC
    }
  } else {
    return null
  }
}

export function findReclaimLink(link: string): EmbedLinkMatch | null {
  const matchesReclaim = isReclaimLink(link)

  if (matchesReclaim?.length) {
    return {
      original: matchesReclaim[0],
      embed: matchesReclaim[0],
      type: EmbedType.RECLAIM
    }
  } else {
    return null
  }
}

function findUcareLink(link: string): EmbedLinkMatch | null {
  const matchesUcare = isUcareLink(link)

  if (matchesUcare?.length) {
    return {
      original: matchesUcare[0],
      embed: matchesUcare[0],
      type: EmbedType.VIDEO
    }
  } else {
    return null
  }
}

const YOUTUBE_HOSTS = new Set(['youtube.com', 'youtube-nocookie.com', 'youtu.be'])

/**
 * Extracts a YouTube video ID from any supported link shape (watch URL in any param order,
 * youtu.be, /embed/, /v/, with or without protocol/www/m prefix), including malformed/legacy
 * stored embed URLs where extra params got glued onto the path without a "?" separator
 * (e.g. ".../embed/ID&t=45s"). Returns null if the host isn't an allowed YouTube host or no ID is found.
 */
export function extractYoutubeVideoId(link: string): string | null {
  if (!link) return null
  const withProtocol = /^https?:\/\//i.test(link) ? link : `https://${link.replace(/^\/\//, '')}`

  let url: URL
  try {
    url = new URL(withProtocol)
  } catch {
    return null
  }

  const host = url.hostname.toLowerCase().replace(/^(www|m)\./, '')
  if (!YOUTUBE_HOSTS.has(host)) return null

  const idFromSegment = (segment: string | null | undefined): string | null => {
    const match = segment?.match(/^[\w-]+/)
    return match ? match[0] : null
  }

  const pathSegments = url.pathname.split('/').filter(Boolean)

  if (host === 'youtu.be') {
    return idFromSegment(pathSegments[0])
  }
  if (pathSegments[0] === 'watch') {
    return idFromSegment(url.searchParams.get('v'))
  }
  if ((pathSegments[0] === 'embed' || pathSegments[0] === 'v') && pathSegments[1]) {
    return idFromSegment(pathSegments[1])
  }
  return null
}

function buildCanonicalYoutubeEmbedUrl(link: string, videoId: string): string {
  const host = /youtube-nocookie\.com/i.test(link) ? 'www.youtube-nocookie.com' : 'www.youtube.com'
  return `https://${host}/embed/${videoId}`
}

export function findYoutubeLink(link: string): EmbedLinkMatch | null {
  const matchesYoutube = isYoutubeLink(link)
  if (!matchesYoutube?.length) return null

  const original = matchesYoutube[0]
  const videoId = extractYoutubeVideoId(original)
  if (!videoId) return null

  return {
    original,
    embed: buildCanonicalYoutubeEmbedUrl(original, videoId),
    type: EmbedType.YOUTUBE
  }
}

/**
 * Render-time, non-destructive repair for legacy/malformed persisted YouTube embed URLs
 * (produced by the pre-fix string-replace bug). Never mutates stored data — callers use the
 * returned value only for display. Falls back to the raw input if extraction fails.
 */
export function normalizeYoutubeEmbedUrl(embedUrl: string): string {
  if (!embedUrl) return embedUrl
  const videoId = extractYoutubeVideoId(embedUrl)
  return videoId ? buildCanonicalYoutubeEmbedUrl(embedUrl, videoId) : embedUrl
}

export function findVimeoLink(link: string): EmbedLinkMatch | null {
  const matchesVimeo = isVimeoLink(link)
  if (!matchesVimeo) return null
  const videoId = matchesVimeo[1]
  // An unlisted video's hash: `vimeo.com/<id>/<hash>`, or `?h=<hash>` on the player link from Vimeo's embed code
  const hash = matchesVimeo[2] ?? link.match(/[?&]h=(\w+)/)?.[1]
  return {
    original: link,
    embed: `https://player.vimeo.com/video/${videoId}${hash ? `?h=${hash}` : ''}`,
    type: EmbedType.VIDEO
  }
}

const VIDEO_FILE_EXTENSION = /\.(mp4|m4v|mov|webm|ogv|ogg|avi|mkv)$/i

/** A link straight to a video file (by its path's extension), not a page on a video platform */
export function isDirectVideoFileLink(link: string): boolean {
  try {
    return VIDEO_FILE_EXTENSION.test(new URL(link).pathname)
  } catch {
    return false
  }
}

export function findCalendlyLink(link: string): EmbedLinkMatch | null {
  const matchesCalendly = isCalendlyLink(link)
  if (matchesCalendly?.length) {
    return {
      original: matchesCalendly[0],
      embed: matchesCalendly[0],
      type: EmbedType.CALENDLY
    }
  } else {
    return null
  }
}

export function findCanvaLink(link: string): EmbedLinkMatch | null {
  const matchesCanva = isCanvaLink(link)
  if (matchesCanva) {
    const baseEmbedUrl = matchesCanva[0]
    const embedUrl = `${baseEmbedUrl}?embed`
    return {
      original: link,
      embed: embedUrl,
      type: EmbedType.CANVA
    }
  }
  return null
}

export function findEmbedLink(link: string): EmbedLinkMatch | null {
  const matchesVideo = findVideoLink(link)
  if (matchesVideo) return prependHttps(matchesVideo)
  const matchesGDocs = findGDocsLink(link)
  if (matchesGDocs) return prependHttps(matchesGDocs)
  const matchesOther = findOtherLinks(link)
  if (matchesOther) return prependHttps(matchesOther)
  const matchesUrl = findUrlLink(link)
  if (matchesUrl) return prependHttps(matchesUrl)

  return null
}

function prependHttps(embedLink: EmbedLinkMatch): EmbedLinkMatch {
  if (!embedLink.embed.startsWith('http')) {
    embedLink.embed = `https://${embedLink.embed}`
  }
  return embedLink
}

export function findVideoLink(link: string): EmbedLinkMatch | null {
  const matchesLoom = findLoomLink(link)
  if (matchesLoom) return matchesLoom
  const matchesYoutube = findYoutubeLink(link)
  if (matchesYoutube) return matchesYoutube
  const matchesVimeo = findVimeoLink(link)
  if (matchesVimeo) return matchesVimeo
  const matchesUcare = findUcareLink(link)
  if (matchesUcare) return matchesUcare

  return null
}

/**
 * Converts an embed URL (iframe src) to the corresponding watch URL for display/QR codes.
 * Returns the original URL if conversion is not applicable.
 */
export function embedUrlToWatchUrl(embedUrl: string, embedType: EmbedType): string {
  if (!embedUrl) return embedUrl
  switch (embedType) {
    case EmbedType.YOUTUBE: {
      const videoId = extractYoutubeVideoId(embedUrl)
      return videoId ? `https://www.youtube.com/watch?v=${videoId}` : embedUrl
    }
    case EmbedType.LOOM:
      return embedUrl.replace('/embed/', '/share/')
    case EmbedType.VIDEO: {
      const vimeoMatch = embedUrl.match(/player\.vimeo\.com\/video\/(\d+)/)
      return vimeoMatch ? `https://vimeo.com/${vimeoMatch[1]}` : embedUrl
    }
    case EmbedType.GDOCS:
      return driveOpenUrl(embedUrl)
    default:
      return embedUrl
  }
}

/** A Drive file's viewer, or a Google Doc's editor: the `/preview` page is the frame, not what people open */
function driveOpenUrl(previewUrl: string): string {
  let host: string
  try {
    host = new URL(previewUrl).hostname
  } catch {
    return previewUrl
  }
  if (host === 'drive.google.com') return previewUrl.replace(/\/preview(?=[?#]|$)/, '/view')
  if (host === 'docs.google.com') return previewUrl.replace(/\/preview(?=[?#]|$)/, '/edit')
  return previewUrl
}

// What the picker can choose but Drive cannot preview
const UNPREVIEWABLE_DRIVE_TYPES = ['application/vnd.google-apps.folder', 'application/vnd.google-apps.shortcut']
const DRIVE_FILE_ID = /^[\w-]{10,}$/

/** The fields of a Google Picker document this app reads */
export type PickedDriveDoc = {
  id?: string
  name?: string
  mimeType?: string
  url?: string
  embedUrl?: string
  isShared?: boolean
}

/**
 * The preview URL to embed for a file chosen in the Google Picker, normalized like a pasted link, or null when Drive
 * cannot preview it. The picker's own `embedUrl` is not always a Drive preview, so its link comes first and the file
 * id is the fallback.
 */
export function drivePickerEmbed(doc: PickedDriveDoc): string | null {
  if (doc.mimeType && UNPREVIEWABLE_DRIVE_TYPES.includes(doc.mimeType)) return null
  for (const link of [doc.url, doc.embedUrl]) {
    const match = link ? findGDocsLink(link) : null
    if (match && /\/preview(?=[?#]|$)/.test(match.embed)) return match.embed
  }
  return doc.id && DRIVE_FILE_ID.test(doc.id) ? `https://drive.google.com/file/d/${doc.id}/preview` : null
}

const GOOGLE_DRIVE_HOSTS = ['drive.google.com', 'docs.google.com']

/**
 * The link on a Google Drive or Docs host, over https (Google serves nothing else), else null. The host is parsed, not
 * searched for in the text: a link that only mentions drive.google.com in its path or query is someone else's page.
 */
function googleDriveUrl(link: string): URL | null {
  const trimmed = link.trim()
  const absolute = /^[a-z][a-z\d+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/\//, '')}`
  let url: URL
  try {
    url = new URL(absolute)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
  const host = url.hostname.replace(/^www\./, '')
  if (!GOOGLE_DRIVE_HOSTS.includes(host)) return null
  return new URL(`https://${host}${url.pathname}${url.search}${url.hash}`)
}

/** Whether the link is on Google Drive or Docs, whatever it points to there (a file, an export, a folder) */
export function isGoogleDriveLink(link: string): boolean {
  return googleDriveUrl(link) !== null
}

/** A link as a short label: host and path, no scheme, query or `www.`. Falls back to the text as typed. */
export function shortLinkLabel(link: string, maxLength = 40): string {
  try {
    const { hostname, pathname } = new URL(link)
    const label = hostname.replace(/^www\./, '') + (pathname === '/' ? '' : pathname)
    return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label
  } catch {
    return link
  }
}

/** The name to show for a linked Google file: its title, or a short link when the stored name is the raw URL. */
export function googleFileDisplayName(name: string | undefined, url: string): string {
  return !name || name === url ? shortLinkLabel(url) : name
}

export function findGDocsLink(link: string): EmbedLinkMatch | null {
  const url = googleDriveUrl(link)
  if (!url) return null
  if (url.hostname === 'drive.google.com') {
    url.pathname = url.pathname.replace(/\/view$/, '/preview')
    return { original: link, embed: url.href, type: EmbedType.GDOCS }
  }
  if (url.pathname.startsWith('/forms/')) {
    // A form has no preview page and its plain page forbids framing: only the published form, as embedded, frames
    const form = url.pathname.match(/^\/forms\/d\/e\/([\w-]+)\/viewform(?:\/|$)/)
    return form
      ? { original: link, embed: `${url.origin}/forms/d/e/${form[1]}/viewform?embedded=true`, type: EmbedType.GDOCS }
      : null
  }
  // An edit or preview link alike; anything after it (query, fragment) is dropped
  const path = url.pathname.split(/\/(?:edit|preview)/)[0]
  return { original: link, embed: `${url.origin}${path}/preview`, type: EmbedType.GDOCS }
}

// One file Google can show in its preview: a Drive file, or a Doc, Sheet, Slides deck or drawing by its id. `/u/1`
// is the signed-in account. Only the shapes `findGDocsLink` turns into a preview URL.
const GOOGLE_PREVIEWABLE_PATH: Record<string, RegExp> = {
  'drive.google.com': /^\/file(?:\/u\/\d+)?\/d\/[\w-]+\/(?:view|preview)$/,
  'docs.google.com':
    /^\/(?:document|spreadsheets|presentation|drawings)(?:\/u\/\d+)?\/d\/[\w-]+(?:\/(?:edit|preview)\/?)?$/
}

/**
 * `findGDocsLink`, only for a link to one file Google's preview can show. Forms, published (`/d/e/…`) and `/embed`
 * links, and folders have no preview page: a `/preview` suffix on them is a 404.
 */
export function findGooglePreviewLink(link: string): EmbedLinkMatch | null {
  const url = googleDriveUrl(link)
  if (!url) return null
  return GOOGLE_PREVIEWABLE_PATH[url.hostname]?.test(url.pathname) ? findGDocsLink(link) : null
}

export function findOtherLinks(link: string): EmbedLinkMatch | null {
  const matchesPitch = findPitchLink(link)
  if (matchesPitch) return matchesPitch
  const matchesTypeform = findTypeformLink(link)
  if (matchesTypeform) return matchesTypeform
  const matchesProductboard = findProductboardLink(link)
  if (matchesProductboard) return matchesProductboard
  const matchesThreeDVista = findThreeDVistaLink(link)
  if (matchesThreeDVista) return matchesThreeDVista
  const matchesCalendly = findCalendlyLink(link)
  if (matchesCalendly) return matchesCalendly
  const matchesReclaim = findReclaimLink(link)
  if (matchesReclaim) return matchesReclaim
  const matchesCanva = findCanvaLink(link)
  if (matchesCanva) return matchesCanva

  return null
}

export function findUrlLink(link: string): EmbedLinkMatch | null {
  if (isUrl(link)) {
    return {
      original: link,
      embed: link,
      type: EmbedType.IFRAME,
      genericUrl: true
    }
  }
  return null
}
